import React, { useState, useCallback, useEffect, useRef } from 'react';
import { X, DollarSign, Upload, AlertCircle, Check, Loader2, FileImage, User, Briefcase, Calendar, ChevronDown, ChevronUp } from 'lucide-react';
import { paymentBatchService } from '../../../services/paymentBatchService';
import { formatCurrency } from '../../../utils/earningsCalculations';

/**
 * RecordPaymentModal
 *
 * Modal for recording a bulk payment with transaction ID and proof of payment.
 * Validates transaction ID uniqueness before submission.
 */
export default function RecordPaymentModal({
  selectedInstances,
  totalAmount,
  jobCount,
  jobDetails = [], // Array of { id, jobName, contractorName, scheduledDate, amount }
  companyId,
  onClose,
  onSuccess
}) {
  const [showJobDetails, setShowJobDetails] = useState(jobCount <= 5);
  const [transactionRef, setTransactionRef] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const debounceTimerRef = useRef(null);

  // Debounced duplicate check with proper cleanup
  useEffect(() => {
    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // If transaction ref is too short, just reset duplicate state
    if (!transactionRef.trim() || transactionRef.length < 3) {
      setIsDuplicate(false);
      setIsCheckingDuplicate(false);
      return;
    }

    // Set up debounced check
    setIsCheckingDuplicate(true);
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const exists = await paymentBatchService.checkTransactionExists(companyId, transactionRef.trim());
        setIsDuplicate(exists);
      } catch (err) {
        console.error('Error checking duplicate:', err);
        setIsDuplicate(false);
      } finally {
        setIsCheckingDuplicate(false);
      }
    }, 500);

    // Cleanup on unmount or when transactionRef changes
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [transactionRef, companyId]);

  // Handle transaction ref input
  const handleTransactionRefChange = (e) => {
    setTransactionRef(e.target.value);
    setError(null);
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please upload an image file (PNG, JPG, etc.)');
        return;
      }
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }
      setProofFile(file);
      setProofPreview(URL.createObjectURL(file));
      setError(null);
    }
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setProofFile(null);
    if (proofPreview) {
      URL.revokeObjectURL(proofPreview);
      setProofPreview(null);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!transactionRef.trim()) {
      setError('Transaction ID is required');
      return;
    }

    if (isDuplicate) {
      setError('This Transaction ID has already been used. Please check your records.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const result = await paymentBatchService.createPaymentBatch({
        companyId,
        transactionRef: transactionRef.trim(),
        totalAmount,
        proofFile,
        jobInstanceIds: selectedInstances
      });

      onSuccess(result);
    } catch (err) {
      if (err.message === 'DUPLICATE_TRANSACTION') {
        setIsDuplicate(true);
        setError('This Transaction ID has already been used. Please check your records.');
      } else {
        setError(err.message || 'Failed to record payment. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center bg-black/70 pt-20 px-4 pb-4 overflow-y-auto">
      <div className="bg-background w-full max-w-lg max-h-[90vh] rounded-xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">Record Payment</h2>
              <p className="text-text-secondary text-sm">
                {jobCount} job{jobCount !== 1 ? 's' : ''} &middot; Total: {formatCurrency(totalAmount)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1">
          {/* Transaction ID Input */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Transaction ID / Reference <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={transactionRef}
                onChange={handleTransactionRefChange}
                placeholder="e.g., TRX-12345 or bank reference number"
                className={`w-full px-4 py-3 bg-surface border rounded-lg text-text-primary placeholder-text-secondary focus:outline-none transition-colors ${
                  isDuplicate
                    ? 'border-red-500 focus:border-red-500'
                    : 'border-border focus:border-primary'
                }`}
                disabled={submitting}
                autoFocus
              />
              {isCheckingDuplicate && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="w-5 h-5 text-text-secondary animate-spin" />
                </div>
              )}
              {!isCheckingDuplicate && transactionRef.length >= 3 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {isDuplicate ? (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <Check className="w-5 h-5 text-green-500" />
                  )}
                </div>
              )}
            </div>
            {isDuplicate && (
              <p className="mt-2 text-sm text-red-400 flex items-center gap-1">
                <AlertCircle size={14} />
                This Transaction ID has already been used. Please check your records.
              </p>
            )}
            <p className="mt-2 text-xs text-text-secondary">
              Enter the bank transfer reference or transaction ID for this payment.
            </p>
          </div>

          {/* Proof of Payment Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Proof of Payment (Optional)
            </label>
            {!proofFile ? (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary hover:bg-surface/50 transition-colors">
                <Upload className="w-8 h-8 text-text-secondary mb-2" />
                <p className="text-sm text-text-secondary">Click to upload receipt or screenshot</p>
                <p className="text-xs text-text-secondary mt-1">PNG, JPG up to 10MB</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  disabled={submitting}
                />
              </label>
            ) : (
              <div className="relative border border-border rounded-lg overflow-hidden">
                <img
                  src={proofPreview}
                  alt="Proof of payment"
                  className="w-full h-40 object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button
                    type="button"
                    onClick={handleRemoveFile}
                    disabled={submitting}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2 transition-colors"
                  >
                    <X size={16} />
                    Remove
                  </button>
                </div>
                <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/60 rounded text-xs text-white flex items-center gap-1">
                  <FileImage size={12} />
                  {proofFile.name}
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Summary with Job Details */}
          <div className="mb-6 p-4 bg-surface rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-text-secondary">Payment Summary</h3>
              <span className="text-lg font-bold text-green-400">{formatCurrency(totalAmount)}</span>
            </div>

            {/* Job Details Toggle */}
            {jobDetails.length > 0 && (
              <div className="border-t border-border pt-3">
                <button
                  type="button"
                  onClick={() => setShowJobDetails(!showJobDetails)}
                  className="flex items-center justify-between w-full text-sm text-text-secondary hover:text-text-primary transition-colors"
                >
                  <span>{jobCount} job{jobCount !== 1 ? 's' : ''} selected</span>
                  {showJobDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {showJobDetails && (
                  <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                    {jobDetails.map((job) => (
                      <div
                        key={job.id}
                        className="flex items-center justify-between p-2 bg-background rounded-lg border border-border/50"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Briefcase size={14} className="text-primary flex-shrink-0" />
                            <span className="text-sm text-text-primary truncate font-medium">
                              {job.jobName || 'Unnamed Job'}
                              {job.instanceNumber && (
                                <span className="text-text-secondary font-normal ml-1">#{job.instanceNumber}</span>
                              )}
                            </span>
                            {job.jobDisplayId && (
                              <span className="text-xs text-text-secondary font-mono">
                                {job.jobDisplayId}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-text-secondary">
                            <span className="flex items-center gap-1">
                              <User size={11} />
                              {job.contractorName || 'Unknown'}
                            </span>
                            {job.scheduledDate && (
                              <span className="flex items-center gap-1">
                                <Calendar size={11} />
                                {new Date(job.scheduledDate).toLocaleDateString('en-AU', {
                                  day: 'numeric',
                                  month: 'short'
                                })}
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="text-sm font-medium text-green-400 ml-3">
                          {formatCurrency(job.amount || 0)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Fallback if no job details provided */}
            {jobDetails.length === 0 && (
              <div className="text-sm text-text-secondary">
                {jobCount} job{jobCount !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-border hover:bg-surface-light text-text-primary rounded-lg transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!transactionRef.trim() || isDuplicate || submitting}
              className="flex items-center gap-2 px-5 py-2 bg-green-600 hover:bg-green-700 disabled:bg-green-600/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <Check size={18} />
                  Confirm Payment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
